"use client";

import { cn } from "@/lib/utils";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Testimonials 1056 — KUK home student testimonials.
 *
 * Port of kuk-campus-portal `components/organisms/Testimonials.tsx`: a ruled
 * heading over a horizontal rail of portrait quote cards. Cards that are not
 * (almost) fully in view dim to 20%, so the rail reads as a spotlight on the
 * centre card. Round prev/next buttons appear only when there is something to
 * scroll to, and a row of dots tracks position below 768px.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:`
 * is 768px and `lg:` is 1024px. This app remaps `md:` to 1230px and exposes
 * 768px as `tab:` (see globals.css `--breakpoint-*`), so the reference's `md:`
 * is ported as `tab:` and its `lg:` as this app's `sm:` (1080px).
 *
 * THE DIM — v1 runs an IntersectionObserver at threshold 0.9 over the rail and
 * keeps the indices of cards at least 90% visible; everything else gets
 * `opacity-20`. Reproduced with the same threshold and the rail as the root, so
 * the effect matches rather than approximating it with scroll maths.
 *
 * THE OPENING SCROLL — v1 scrolls the rail on mount (and on every resize) so it
 * does not start at card one: by one card width plus the gap on mobile, by two
 * card widths above it. That is what puts a lit card in the middle of the frame
 * on load, so it is reproduced. It is a `scrollTo` on the rail only — the page
 * itself never moves.
 *
 * CARD TINT — v1 fills a lit card white below 768px and brand-maroon-at-10%
 * above it, which is what makes the rail read as cards on a phone and as a
 * tinted strip on a desktop. Both are CMS colours here.
 *
 * DROPPED — v1's `overflow-scroll` on the quote paragraph, which makes a long
 * quote scroll inside the card with a hidden scrollbar, i.e. silently
 * unreachable copy. The card is a fixed height, so the quote clamps instead.
 */

interface TestimonialRow {
  id?: string | number;
  meta_image?: string;
  meta_alt_image?: string;
  meta_name?: string;
  meta_designation?: string;
  meta_quote?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface Testimonials1056Data {
  metadata?: TestimonialRow[];
  /** Section heading. */
  title?: string;
  title_start_color?: string;
  /** Accent rule under the heading. */
  gradient_color_1?: string;
  /** Band behind the section. */
  background_color?: string;
  /** Lit card fill from 768px up. */
  card_color?: string;
  /** Lit card fill below 768px. */
  mobile_background_color?: string;
  /** The large opening quote glyph. */
  title_end_color?: string;
  /** Quote body. */
  gradient_color_2?: string;
  /** Student name. */
  gradient_color_3?: string;
  /** Designation. */
  gradient_color_4?: string;
  /** Prev / next pill background and glyph. */
  button_color?: string;
  button_text_color?: string;
  /** Optional custom arrow art; empty uses the reference's ← / → glyphs. */
  left_navigation_icon?: string;
  left_navigation_icon_alt?: string;
  right_navigation_icon?: string;
  right_navigation_icon_alt?: string;
  /** Dots below 768px — in view / out of view. */
  pre_title_color?: string;
  desktop_background_color?: string;
  status?: boolean;
}

/** v1's `.custom-underline`: `bottom:-6px; left:0; width:33%; height:5px`. */
const HEADING_UNDERLINE = "absolute -bottom-1.5 left-0 h-[5px] w-1/3";

/** v1 tolerates a sub-pixel gap before hiding an arrow. */
const SCROLL_EPSILON = 1;
/** v1's fixed gap between cards when computing the opening scroll. */
const MOBILE_GAP = 30;
/** v1 switches its scroll maths at the raw 768px breakpoint. */
const TAB = 768;

const Testimonials1056 = ({ data }: { data: any }) => {
  const sectionData = (data?.data ?? data) as Testimonials1056Data | undefined;

  const {
    title = "",
    title_start_color = "#031F44",
    gradient_color_1 = "#9F1C33",
    background_color = "#FFFFFF",
    card_color = "rgba(159, 28, 51, 0.1)",
    mobile_background_color = "#FFFFFF",
    title_end_color = "#031F44",
    gradient_color_2 = "#344054",
    gradient_color_3 = "#031F44",
    gradient_color_4 = "#475467",
    button_color = "#FFFFFF",
    button_text_color = "#031F44",
    left_navigation_icon = "",
    left_navigation_icon_alt = "Previous",
    right_navigation_icon = "",
    right_navigation_icon_alt = "Next",
    pre_title_color = "#031F44",
    desktop_background_color = "#6B7280",
  } = sectionData || {};

  const status = isCmsShowFlagOn(sectionData?.status, true);

  const testimonials = useMemo(() => {
    const rows = Array.isArray(sectionData?.metadata) ? sectionData!.metadata! : [];
    return rows
      .filter((r) => r?.meta_status !== false)
      .sort(
        (a, b) => (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata]);

  const railRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState<number[]>([]);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(true);

  const count = testimonials.length;

  // v1: threshold 0.9, the rail as root — a card counts as "lit" at ≥90% visible.
  useEffect(() => {
    const rail = railRef.current;
    if (!rail || count === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        setVisible((prev) => {
          const next = new Set(prev);
          entries.forEach((entry) => {
            const i = Number((entry.target as HTMLElement).dataset.index);
            if (Number.isNaN(i)) return;
            if (entry.isIntersecting) next.add(i);
            else next.delete(i);
          });
          return Array.from(next);
        });
      },
      { root: rail, threshold: 0.9 },
    );

    rail.querySelectorAll("[data-testimonial-card]").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [count]);

  // v1 scrolls the rail off card one on mount and on every resize.
  useEffect(() => {
    const rail = railRef.current;
    if (!rail || count === 0) return undefined;

    const openingScroll = () => {
      const cards = rail.querySelectorAll<HTMLElement>("[data-testimonial-card]");
      if (cards.length === 0) return;
      const cardWidth = cards[0].clientWidth;
      const isMobile = window.innerWidth < TAB;
      rail.scrollTo({
        left: isMobile ? cardWidth + MOBILE_GAP : cardWidth * 2,
        behavior: "smooth",
      });
    };

    const handleScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = rail;
      setShowLeft(scrollLeft > SCROLL_EPSILON);
      setShowRight(scrollLeft < scrollWidth - clientWidth - SCROLL_EPSILON);
    };

    openingScroll();
    handleScroll();
    rail.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", openingScroll);
    return () => {
      rail.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", openingScroll);
    };
  }, [count]);

  const scroll = useCallback((direction: "left" | "right") => {
    const rail = railRef.current;
    if (!rail) return;
    const first = rail.querySelector<HTMLElement>("[data-testimonial-card]");
    if (!first) return;
    const cardWidth = first.clientWidth;
    const containerWidth = rail.clientWidth;
    const isMobile = window.innerWidth < TAB;
    const amount = isMobile
      ? cardWidth + 20
      : cardWidth * 2 - (containerWidth - cardWidth * 2) / 2;
    rail.scrollBy({ left: direction === "left" ? -amount : amount, behavior: "smooth" });
  }, []);

  if (!status || count === 0) return null;

  const arrowClass =
    "absolute top-1/2 z-10 -translate-y-1/2 rounded-full p-2 shadow-md";

  return (
    <section className="w-full" style={{ backgroundColor: background_color }}>
      <div className="relative mx-auto w-full max-w-[1120px] overflow-hidden">
        {title && (
          <div className="p-10 tab:px-20 tab:py-10">
            <h2
              className="relative w-fit text-2xl font-bold tab:text-4xl"
              style={{ color: title_start_color }}
            >
              {title}
              <span
                aria-hidden
                className={HEADING_UNDERLINE}
                style={{ backgroundColor: gradient_color_1 }}
              />
            </h2>
          </div>
        )}

        <div className="relative w-full">
          <div
            ref={railRef}
            className={cn(
              "flex justify-around space-x-4 overflow-x-auto py-10",
              "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
            )}
          >
            {testimonials.map((t, i) => {
              const lit = visible.includes(i);
              return (
                <div
                  key={t.id ?? i}
                  data-testimonial-card
                  data-index={i}
                  className={cn(
                    "mx-2 flex h-[420px] w-[300px] shrink-0 flex-col items-center justify-between",
                    "rounded-md p-10 text-center shadow-xl transition-opacity duration-300",
                    "tab:min-h-[375px] tab:min-w-[400px]",
                    // v1 fills a lit card white below `tab` and tinted above it.
                    // Both colours ride in as custom properties and the variant
                    // picks which one `--kuk-card` resolves to.
                    "[--kuk-card:var(--kuk-card-mobile)] tab:[--kuk-card:var(--kuk-card-desktop)]",
                    !lit && "opacity-20",
                  )}
                  style={{
                    ["--kuk-card-mobile" as string]: mobile_background_color,
                    ["--kuk-card-desktop" as string]: card_color,
                    backgroundColor: "var(--kuk-card)",
                  }}
                >
                  {t.meta_image && (
                    <div className="relative size-[75px] shrink-0">
                      <Image
                        src={t.meta_image}
                        alt={t.meta_alt_image || t.meta_name || "Student"}
                        fill
                        loading="lazy"
                        sizes="75px"
                        className="rounded-full object-cover"
                      />
                    </div>
                  )}

                  <p
                    aria-hidden
                    className="m-0 flex w-full justify-start p-0 text-left text-5xl font-bold"
                    style={{ color: title_end_color }}
                  >
                    &quot;
                  </p>

                  {t.meta_quote && (
                    <p
                      className="mb-4 overflow-hidden font-normal"
                      style={{ color: gradient_color_2 }}
                    >
                      {t.meta_quote}
                    </p>
                  )}

                  <div>
                    {t.meta_name && (
                      <p
                        className="text-sm font-bold tab:text-base"
                        style={{ color: gradient_color_3 }}
                      >
                        {t.meta_name}
                      </p>
                    )}
                    {t.meta_designation && (
                      <p
                        className="text-xs font-semibold tab:text-sm"
                        style={{ color: gradient_color_4 }}
                      >
                        {t.meta_designation}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {showLeft && (
            <button
              type="button"
              aria-label={left_navigation_icon_alt || "Previous"}
              onClick={() => scroll("left")}
              className={cn(arrowClass, "left-0")}
              style={{ backgroundColor: button_color, color: button_text_color }}
            >
              {left_navigation_icon ? (
                <Image
                  src={left_navigation_icon}
                  alt={left_navigation_icon_alt || "Previous"}
                  width={16}
                  height={16}
                />
              ) : (
                "←"
              )}
            </button>
          )}
          {showRight && (
            <button
              type="button"
              aria-label={right_navigation_icon_alt || "Next"}
              onClick={() => scroll("right")}
              className={cn(arrowClass, "right-0")}
              style={{ backgroundColor: button_color, color: button_text_color }}
            >
              {right_navigation_icon ? (
                <Image
                  src={right_navigation_icon}
                  alt={right_navigation_icon_alt || "Next"}
                  width={16}
                  height={16}
                />
              ) : (
                "→"
              )}
            </button>
          )}

          <div className="absolute bottom-0 left-1/2 flex -translate-x-1/2 space-x-2 tab:hidden">
            {testimonials.map((t, i) => (
              <span
                key={t.id ?? i}
                className="size-2 rounded-full"
                style={{
                  backgroundColor: visible.includes(i)
                    ? pre_title_color
                    : desktop_background_color,
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials1056;
